import {
  Button,
  Checkbox,
  Loading,
  templateToString,
  useDisposable,
} from '@affine/component';
import { Pagination } from '@affine/component/setting-components';
import { BlobManagementService } from '@affine/core/modules/blob-management/services';
import { useI18n } from '@affine/i18n';
import type { ListedBlobRecord } from '@affine/nbstore';
import { getAttachmentFileIcon } from '@blocksuite/affine/components/icons';
import { DownloadIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import bytes from 'bytes';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as styles from './style.css';

const PAGE_SIZE = 9;

const useBlob = (blobRecord: ListedBlobRecord) => {
  const unusedBlobsEntity = useService(BlobManagementService).unusedBlobs;
  return useDisposable(
    (abortSignal?: AbortSignal) =>
      unusedBlobsEntity.hydrateBlob(blobRecord, abortSignal),
    [blobRecord]
  );
};

const getDownloadFilename = (blobKey: string, extension?: string) => {
  if (!extension || blobKey.toLowerCase().endsWith(`.${extension}`)) {
    return blobKey;
  }

  return `${blobKey}.${extension}`;
};

const BlobPreview = ({
  backendAvailable,
  blobRecord,
  downloadLabel,
}: {
  backendAvailable: boolean;
  blobRecord: ListedBlobRecord;
  downloadLabel: string;
}) => {
  const { data, loading, error } = useBlob(blobRecord);

  const element = useMemo(() => {
    const icon = templateToString(
      getAttachmentFileIcon(data?.type ?? 'unknown')
    );

    if (loading && backendAvailable) return <Loading size={24} />;
    if (!data?.url || !data.type || (loading && !backendAvailable)) {
      return (
        <div
          className={styles.unknownBlobIcon}
          dangerouslySetInnerHTML={{ __html: icon }}
        />
      );
    }

    const { url, mime } = data;

    if (error) {
      return (
        <div
          className={styles.unknownBlobIcon}
          dangerouslySetInnerHTML={{ __html: icon }}
        />
      );
    }

    if (mime?.startsWith('image/')) {
      return (
        <img
          className={styles.blobImagePreview}
          src={url}
          alt={blobRecord.key}
        />
      );
    }

    return (
      <div
        className={styles.unknownBlobIcon}
        dangerouslySetInnerHTML={{ __html: icon }}
      />
    );
  }, [backendAvailable, blobRecord.key, data, error, loading]);

  return (
    <div className={styles.blobPreviewContainer}>
      <div className={styles.blobPreview}>{element}</div>
      <div className={styles.blobPreviewFooter}>
        <div className={styles.blobPreviewName} title={blobRecord.key}>
          {blobRecord.key}
        </div>
        <div className={styles.blobPreviewInfo}>
          {data?.type ? `${data.type} · ` : ''}
          {bytes(blobRecord.size)}
        </div>
        {data?.url ? (
          <a
            className={styles.blobDownloadLink}
            href={data.url}
            download={getDownloadFilename(blobRecord.key, data.extension)}
            onClick={event => event.stopPropagation()}
            title={downloadLabel}
          >
            <DownloadIcon className={styles.blobDownloadIcon} />
            <span>{downloadLabel}</span>
          </a>
        ) : null}
      </div>
    </div>
  );
};

const BlobCard = ({
  backendAvailable,
  blobRecord,
  downloadLabel,
  onClick,
  selectable,
  selected,
}: {
  backendAvailable: boolean;
  blobRecord: ListedBlobRecord;
  downloadLabel: string;
  onClick?: (e: ReactMouseEvent) => void;
  selectable: boolean;
  selected: boolean;
}) => {
  return (
    <div
      data-testid="blob-preview-card"
      className={styles.blobCard}
      data-selected={selected}
      data-selectable={selectable}
      onClick={onClick}
    >
      {selectable ? (
        <Checkbox className={styles.blobGridItemCheckbox} checked={selected} />
      ) : null}
      <BlobPreview
        backendAvailable={backendAvailable}
        blobRecord={blobRecord}
        downloadLabel={downloadLabel}
      />
    </div>
  );
};

export interface StorageFileGridSelectionContext {
  clearSelection: () => void;
  selectedFiles: ListedBlobRecord[];
}

export interface StorageFileGridProps {
  backendAvailable: boolean;
  description?: string;
  emptyText: string;
  files: ListedBlobRecord[];
  hideEmpty?: boolean;
  loading?: boolean;
  renderSelectionActions?: (
    context: StorageFileGridSelectionContext
  ) => ReactNode;
  selectable?: boolean;
  selectedFiles?: ListedBlobRecord[];
  selectAllLabel?: string;
  selectedLabel?: string;
  title: string;
  onSelectedFilesChange?: (files: ListedBlobRecord[]) => void;
}

export const StorageFileGrid = ({
  backendAvailable,
  description,
  emptyText,
  files,
  hideEmpty = false,
  loading = false,
  renderSelectionActions,
  selectable = false,
  selectedFiles = [],
  selectAllLabel,
  selectedLabel,
  title,
  onSelectedFilesChange,
}: StorageFileGridProps) => {
  const t = useI18n();
  const [pageNum, setPageNum] = useState(0);
  const [selectionAnchor, setSelectionAnchor] =
    useState<ListedBlobRecord | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const skip = pageNum * PAGE_SIZE;
  const pageFiles = useMemo(
    () => files.slice(skip, skip + PAGE_SIZE),
    [files, skip]
  );
  const selectedKeys = useMemo(
    () => new Set(selectedFiles.map(file => file.key)),
    [selectedFiles]
  );
  const isSelectable = selectable && !!onSelectedFilesChange;

  useEffect(() => {
    if (skip >= files.length && pageNum > 0) {
      setPageNum(Math.max(0, Math.ceil(files.length / PAGE_SIZE) - 1));
    }
  }, [files.length, pageNum, skip]);

  useEffect(() => {
    if (!isSelectable || !gridRef.current) return;
    const clearOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!gridRef.current?.contains(target)) {
        onSelectedFilesChange?.([]);
      }
    };
    document.addEventListener('click', clearOnOutsideClick);
    return () => document.removeEventListener('click', clearOnOutsideClick);
  }, [isSelectable, onSelectedFilesChange]);

  const handleSelectFile = useCallback(
    (file: ListedBlobRecord) => {
      onSelectedFilesChange?.(
        selectedKeys.has(file.key)
          ? selectedFiles.filter(item => item.key !== file.key)
          : [...selectedFiles, file]
      );
    },
    [onSelectedFilesChange, selectedFiles, selectedKeys]
  );

  const handleFileClick = useCallback(
    (file: ListedBlobRecord, event: ReactMouseEvent) => {
      if (!isSelectable) return;
      event.stopPropagation();

      if (event.shiftKey && selectionAnchor) {
        const anchorIndex = pageFiles.findIndex(
          item => item.key === selectionAnchor.key
        );
        const currentIndex = pageFiles.findIndex(item => item.key === file.key);
        if (anchorIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(anchorIndex, currentIndex);
          const end = Math.max(anchorIndex, currentIndex);
          const range = pageFiles.slice(start, end + 1);
          const keepExisting = event.metaKey || event.ctrlKey;
          const next = new Map(
            (keepExisting ? selectedFiles : []).map(item => [item.key, item])
          );
          range.forEach(item => next.set(item.key, item));
          onSelectedFilesChange?.([...next.values()]);
          return;
        }
      }

      handleSelectFile(file);
      if (selectedFiles.length === 0) {
        setSelectionAnchor(selectedKeys.has(file.key) ? null : file);
      }
    },
    [
      handleSelectFile,
      isSelectable,
      onSelectedFilesChange,
      pageFiles,
      selectedFiles,
      selectedKeys,
      selectionAnchor,
    ]
  );

  const handleSelectAll = useCallback(
    (event: ReactMouseEvent) => {
      event.stopPropagation();
      const next = new Map(selectedFiles.map(item => [item.key, item]));
      pageFiles.forEach(item => next.set(item.key, item));
      onSelectedFilesChange?.([...next.values()]);
    },
    [onSelectedFilesChange, pageFiles, selectedFiles]
  );

  const clearSelection = useCallback(() => {
    onSelectedFilesChange?.([]);
  }, [onSelectedFilesChange]);

  const showSelectAll =
    isSelectable && pageFiles.some(file => !selectedKeys.has(file.key));
  const showSelectionControls = isSelectable && selectedFiles.length > 0;

  return (
    <>
      {showSelectionControls ? (
        <div className={styles.blobManagementControls}>
          <div className={styles.blobManagementName}>
            {`${selectedFiles.length} ${selectedLabel ?? ''}`.trim()}
          </div>
          <div className={styles.spacer} />
          {showSelectAll ? (
            <Button onClick={handleSelectAll} variant="primary">
              {selectAllLabel}
            </Button>
          ) : null}
          {renderSelectionActions?.({ clearSelection, selectedFiles })}
        </div>
      ) : (
        <div className={styles.blobManagementNameInactive}>
          <div>{`${title} (${files.length})`}</div>
          {description ? (
            <div className={styles.blobManagementDescription}>
              {description}
            </div>
          ) : null}
        </div>
      )}

      {!loading && files.length === 0 ? (
        hideEmpty ? null : (
          <div className={styles.empty}>{emptyText}</div>
        )
      ) : (
        <div className={styles.blobManagementContainer}>
          {loading && backendAvailable ? (
            <div className={styles.loadingContainer}>
              <Loading size={32} />
            </div>
          ) : (
            <>
              <div className={styles.blobPreviewGrid} ref={gridRef}>
                {pageFiles.map(file => (
                  <BlobCard
                    backendAvailable={backendAvailable}
                    blobRecord={file}
                    downloadLabel={t.t(
                      'com.streamlain.settings.workspace.storage.file.download'
                    )}
                    key={file.key}
                    onClick={
                      isSelectable
                        ? event => handleFileClick(file, event)
                        : undefined
                    }
                    selectable={isSelectable}
                    selected={selectedKeys.has(file.key)}
                  />
                ))}
              </div>
              {files.length > PAGE_SIZE ? (
                <Pagination
                  pageNum={pageNum}
                  totalCount={files.length}
                  countPerPage={PAGE_SIZE}
                  onPageChange={(_, nextPageNum) => {
                    setPageNum(nextPageNum);
                  }}
                />
              ) : null}
            </>
          )}
        </div>
      )}
    </>
  );
};
